import { Alert } from '@dbrain/ui';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ApiClientError } from '../api/client.js';
import { useCompanyZone } from '../api/hooks.js';

/**
 * Shows an error in plain language with a suggested action; the code,
 * status and request id stay folded under "technical details" (D-39).
 */
export function ErrorAlert({ error, action }: { error: unknown; action?: ReactNode }) {
  const { t } = useTranslation();
  const zone = useCompanyZone();
  if (!error) return null;
  const apiError =
    error instanceof ApiClientError ? error : new ApiClientError('internal_error', 0, (error as Error)?.message ?? String(error));
  // SA-023: a used-up budget says which one and when it resets, instead of minutes to wait
  const quota = apiError.code === 'quota_exceeded' ? apiError.quota : undefined;
  const minutes = !quota && apiError.retryAfter ? Math.max(1, Math.ceil(apiError.retryAfter / 60)) : undefined;
  return (
    <Alert
      tone="error"
      title={t(`errors.${apiError.code}.title`)}
      detailsLabel={t('common.technicalDetails')}
      details={
        <>
          <div>
            {t('common.errorCode')}: {apiError.code}
            {apiError.status ? ` (HTTP ${apiError.status})` : ''}
          </div>
          {apiError.requestId ? (
            <div>
              {t('common.requestId')}: {apiError.requestId}
            </div>
          ) : null}
          {apiError.message && apiError.message !== apiError.code ? <div>{apiError.message}</div> : null}
        </>
      }
      action={action}
      data-testid={`error-${apiError.code}`}
    >
      {quota ? (
        <p>
          {t(`quotaNotice.${quota.scope}`)}{' '}
          <Trans i18nKey="quotaNotice.resets" values={{ when: new Date(quota.resetsAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short', timeZone: zone }) }} components={{ b: <strong /> }} />
          {quota.scope === 'tenant_monthly' ? ` ${t('quotaNotice.adminCanRaise')}` : ''}
        </p>
      ) : (
        <p>{t(`errors.${apiError.code}.action`)}</p>
      )}
      {minutes ? <p>{t('errors.retryAfter', { minutes })}</p> : null}
    </Alert>
  );
}

/** Field-level message for validation issues returned by the API. */
export function fieldError(error: unknown, path: string, t: (key: string) => string): string | undefined {
  if (!(error instanceof ApiClientError) || !error.fields) return undefined;
  const issue = error.fields.find((f) => f.path === path);
  if (!issue) return undefined;
  if (path === 'email') return t('fields.email');
  return t('fields.invalid');
}
